<script setup lang="ts">
import { computed, reactive, watch, inject } from "vue";
import useVuelidate from "@vuelidate/core";

import { conditionComponents } from "./index";
import { Suggestion } from "@/entities/suggestion";
import { MedicalCondition, PersonData, PrescribedConditionTypes } from "@/entities/conditions";
import MedicalConditionSearch from "./MedicalConditionSearch.vue";
import MedicalConditionList from "./MedicalConditionList.vue";
import Modal from "@/components/Modal.vue";
import { useToast } from "@/services/toast";
import { useConfigStore } from "@/stores/config";
import { sanitizeString } from "@/utils/string";
import { formNavigationKey } from "../keys";

export type MedicalState = {
    conditionSuggestions: Suggestion[],
    currentCondition: MedicalCondition | null,
    isEdit: boolean,
    isConditionCancelling: boolean,
    subConditionSuggestion: Suggestion | null
}

export interface Props {
    medicalConditions: MedicalCondition[],
    personDetails: PersonData
}

const props = defineProps<Props>();
const store = useConfigStore();
const { toast } = useToast();

const emit = defineEmits<{
        (on:"update:conditions", value: MedicalCondition[]): void
    }>();

const state: MedicalState = reactive({
    conditionSuggestions: [] as Suggestion[],
    subConditionSuggestion: null,
    currentCondition: null,
    isConditionCancelling: false,
    isEdit: false,
});

const v$ = useVuelidate();

const handleSaveCondition = async () => {
    state.isConditionCancelling = false;
    const isFormCorrect = await v$.value.$validate();
    if (!isFormCorrect) {
        return;
    }
    if (state.currentCondition !== null) {
        const newConditions = [...props.medicalConditions];
        const editingIndex = newConditions.findIndex(cond => cond.name === state.currentCondition?.name);
        if (editingIndex >= 0) {
            newConditions[editingIndex] = state.currentCondition;
        } else {
            newConditions.push(state.currentCondition);
        }
        state.currentCondition = null;
        state.subConditionSuggestion = null;
        state.isEdit = false;
        emit("update:conditions", newConditions);
    }
};

const handleCancelCondition = () => {
    state.isConditionCancelling = false;
    state.currentCondition = null;
    state.subConditionSuggestion = null;
    state.isEdit = false;
};

const applySuggestion = async (condition: Suggestion) => {
    const isDuplicateCondition = props.medicalConditions
        .some((suggestion: Suggestion) => suggestion.name === condition.name);
    if (isDuplicateCondition) {
        toast({
            timeout: store.toastTimeout,
            title: "Error",
            message: "Could not add a duplicate condition.",
        });
        return;
    }

    state.isEdit = false;
    const existingMedical = props.medicalConditions.find(cond => cond.type === condition.type);
    if (existingMedical && "subConditions" in existingMedical) {
        state.subConditionSuggestion = condition;
        state.isEdit = true;
        state.currentCondition = existingMedical as MedicalCondition;

        return;
    }

    if (existingMedical?.type === PrescribedConditionTypes.Stroke) {
        state.isEdit = true;
        state.currentCondition = existingMedical as MedicalCondition;

        return;
    }
    state.currentCondition = condition as MedicalCondition;
};

const handleConditionChange = (condition: MedicalCondition) => {
    const initialSubConditionsLength = state.currentCondition?.subConditions?.length || 0;
    const isSubConditionDeleted = (state.isEdit && condition.subConditions &&
    condition.subConditions.length < initialSubConditionsLength) ||
    (condition.subConditions?.length === 0 && initialSubConditionsLength === 0);
    state.currentCondition = condition;
    state.subConditionSuggestion = null;
    if (isSubConditionDeleted) {
        state.currentCondition = null;
        handleSubConditionDelete(condition);
    }
};

const handleSubConditionDelete = (condition: MedicalCondition) => {
    const newConditions = [...props.medicalConditions];
    const editingIndex = newConditions.findIndex(cond => cond.type === condition?.type);

    if (editingIndex === -1) {
        return;
    }

    if (condition.subConditions?.length === 0) {
        newConditions.splice(editingIndex, 1);
        state.isEdit = false;
        state.currentCondition = null;
    } else if (condition.subConditions) {
        const firstSubCondition = condition.subConditions[0];
        condition.id = firstSubCondition.id;
        condition.name = firstSubCondition.name;
        condition.code = firstSubCondition.code;
        newConditions[editingIndex] = condition;
        state.currentCondition = condition;
    }

    emit("update:conditions", newConditions);
};

const onEdit = (condition: MedicalCondition) => {
    state.currentCondition = condition;
    state.subConditionSuggestion = null;
    state.isEdit = true;
};

const onDelete = (condition: MedicalCondition) => {
    const newConditions = [...props.medicalConditions].filter(cond => cond.name !== condition.name);
    state.currentCondition = null;
    state.subConditionSuggestion = null;
    emit("update:conditions", newConditions);
};

const currentMedicalComponent = computed(() => {
    const component = conditionComponents.find(
        (component) => component.type === state.currentCondition?.type,
    )?.component;

    return {
        type: state.currentCondition?.type,
        component,
    };
});

const isEditing = computed(() => state.currentCondition !== null);
const formNavigation = inject(formNavigationKey);

watch(isEditing, (newVal) => {
    if (newVal) {
        formNavigation?.disable("You have unsaved medical changes.");
    } else {
        formNavigation?.enable();
    }
});

</script>
<template>
    <div class="alert alert-info">
        <p>
            To declare a condition begin typing its name into the box below and select it from the dropdown list. If your condition is not listed, type in its name and select 'Condition is not listed'.
        </p>
    </div>
    <div class="border border-info rounded p-3 medical-frame medical-frame">
        <div class="form-group bg-blue col-md-12 mx-auto">
            <MedicalConditionList
                :conditions="props.medicalConditions"
                :condition-to-edit="state.currentCondition"
                @edit:condition="onEdit"
                @delete:condition="onDelete"
                @cancel:edit:condition="handleCancelCondition"
                @save:condition="handleSaveCondition"
            >
                <template
                    v-if="!state.isEdit && state.currentCondition?.name"
                    #default
                >
                    <div class="position-relative">
                        <button
                            :id="`close-${sanitizeString(state.currentCondition?.name)}`"
                            type="button"
                            class="close-1 position-absolute end-0 top-0 mt-2"
                            aria-label="close"
                            @click.prevent="state.isConditionCancelling = true"
                        >
                            <span aria-hidden="true">&times;</span>
                        </button>
                        <component
                            :is="(currentMedicalComponent.component as unknown)"
                            :key="state.currentCondition?.name"
                            :condition="state.currentCondition"
                            :sub-condition-suggestion="state.subConditionSuggestion"
                            :person-details="personDetails"
                            @update:condition="handleConditionChange"
                        />
                        <button
                            id="addCondition"
                            type="button"
                            class="btn btn-primary btn-block font-weight-bold"
                            @keydown.enter="handleSaveCondition"
                            @click="handleSaveCondition"
                        >
                            Save Condition
                        </button>
                        <Modal
                            id="condition-cancel-modal"
                            header="Before you close"
                            open-focus="close-condition-cancel-modal"
                            :show="state.isConditionCancelling"
                            @close="state.isConditionCancelling = false"
                        >
                            <template #body>
                                <p>Do you want to save your changes or close without saving</p>
                            </template>
                            <template #footer>
                                <button
                                    :id="`cancelCondition_${sanitizeString(state.currentCondition?.name)}`"
                                    type="button"
                                    class="btn btn-danger font-weight-bold mx-auto mx-sm-0"
                                    @click.prevent="handleCancelCondition"
                                >
                                    Close and lose unsaved changes
                                </button>
                                <button
                                    :id="`confirmCondition_${sanitizeString(state.currentCondition?.name)}`"
                                    type="button"
                                    class="btn btn-primary font-weight-bold mx-auto mx-sm-0"
                                    @click.prevent="handleSaveCondition"
                                >
                                    Save changes
                                </button>
                            </template>
                        </Modal>
                    </div>
                </template>
                <template
                    v-else
                    #[sanitizeString(state.currentCondition?.name)]
                >
                    <component
                        :is="(currentMedicalComponent.component as unknown)"
                        v-if="state.currentCondition?.name"
                        :key="state.currentCondition?.name"
                        :condition="state.currentCondition"
                        :sub-condition-suggestion="state.subConditionSuggestion"
                        :person-details="personDetails"
                        @update:condition="handleConditionChange"
                    />
                    <div class="row">
                        <div class="col-12">
                            <button
                                id="addCondition"
                                type="button"
                                class="btn btn-primary btn-block font-weight-bold col-md-3"
                                @click="handleSaveCondition"
                            >
                                Save Condition
                            </button>
                        </div>
                    </div>
                </template>
            </MedicalConditionList>
            <MedicalConditionSearch
                v-if="state.currentCondition === null"
                id="medicalCondition"
                :key="medicalConditions.length"
                label="Enter a medical condition"
                @select:condition="applySuggestion"
            />
        </div>
    </div>
</template>
